import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useCommentOnDecisionGroupMutationHook, useSubmitMarginDecisionMutationHook } from "@/hooks";
import { nonEmptyString, onInvalid } from "@/utils/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState, useRef, JSX, RefObject } from "react";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { TFunction } from "i18next";
import { DropdownSelect } from "@/components/shared";
import { CommentEnum, MarginDecisionEnum, MarginDecisionGroupFragment, MarginDecisionGroupPermissionEnum, TenderState, UserOnEntity } from "@/graphql/__generated__/graphql";
import { useFetchMarginDecisionsByGroupPolling } from "@/hooks/queries";
import { ChatMentionsPopup } from "@/components/shared/mentions";
import { handleOnKeyDown } from "@/utils/helpers";
import { handleMentionDetection, transformMessageForSubmission, filterUsersByMention } from "@/components/shared/mentions/utils";
import { TriangleAlert } from "lucide-react";
import DecisionConfirmationAlertDialog from "./DecisionConfirmationAlertDialog";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";
import { getAwaitingOrganizationName, isSkipOption, prefixSkipComment, SKIP_APPROVE, SKIP_REJECT } from "../../utils/decisionSkip";

interface Props {
  marginDecisionGroupData: MarginDecisionGroupFragment;
  tenderState: TenderState;
  tenderUsers: UserOnEntity[];
  userHasPermission: boolean;
};

// Zod schema creation for validating margin decision inputs
const createMarginDecisionSchema = (t: TFunction) =>
  z.object({
    decision: z.preprocess(
      (val) => (val === "" || val === null ? undefined : val),
      z.enum([
        MarginDecisionEnum.Approve,
        MarginDecisionEnum.Reject,
        MarginDecisionEnum.Comment,
        SKIP_APPROVE,
        SKIP_REJECT,
      ])
    ),
    comment: nonEmptyString(t("chatinputbar.comment")),
  });

const MarginDecisionDefaultValues = {
  decision: MarginDecisionEnum.Comment as any,
  comment: "",
};

/**
 * @component ChatInputBar
 * @description Renders an input bar for users to submit their tender decisions.
 *
 * @param {Props} props - The properties for the component.
 *
 * @returns {JSX.Element} The rendered ChatInputBar component.
 */
const ChatInputBar = ({ userHasPermission, marginDecisionGroupData, tenderUsers, tenderState }: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const currentLanguage = localStorage.getItem("i18nextLng");
  const { marginDecisionGroupID, tenderID } = useParams<{ marginDecisionGroupID: string, tenderID: string }>();

  // States
  const [decisionConfirmationOpen, setDecisionConfirmationOpen] = useState<boolean>(false);

  // Zod schema
  const MarginDecisionSchema = createMarginDecisionSchema(t);
  type TMarginDecisionSchema = z.infer<typeof MarginDecisionSchema>;

  const form= useForm<TMarginDecisionSchema>({
  // const { register, handleSubmit, setValue, trigger, watch, reset, formState: { errors, isValid } } = useForm<TMarginDecisionSchema>({
    resolver: zodResolver(MarginDecisionSchema) as Resolver<TMarginDecisionSchema>,
    defaultValues: MarginDecisionDefaultValues,
    mode: "onChange",
  });

  // Constants
  const hasFinalDecision = marginDecisionGroupData?.awaitingOrganizationId === null; // Check if the user is the final participant
  // Permissions
  const canDecide = marginDecisionGroupData?.permissions?.includes(MarginDecisionGroupPermissionEnum.Decide);
  const canDecideAheadOfTurn = marginDecisionGroupData?.permissions?.includes(MarginDecisionGroupPermissionEnum.DecideAheadOfTurn);
  const canComment = marginDecisionGroupData?.permissions?.includes(MarginDecisionGroupPermissionEnum.Comment);
  const awaitingOrganizationName = getAwaitingOrganizationName(
    marginDecisionGroupData?.participantsOnDecisionGroup,
    marginDecisionGroupData?.awaitingOrganizationId
  );
  const skipOrganizationLabel = awaitingOrganizationName ?? t("chatinputbar.waitingorganization");

  // Watch the form values
  const decisionValue = form.watch("decision");
  const commentValue = form.watch("comment");
  const isSkipSelected = isSkipOption(decisionValue);

  // New state & ref for mentions in the comment field
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLInputElement | null>(null);

  // Destructure the comment field registration to override onChange/ref
  const {
    ref: commentRef,
    onChange: commentOnChange,
    ...commentRest
  } = form.register("comment");

  const filteredUsers = filterUsersByMention(mentionQuery, tenderUsers);

  // Reset the form when marginDecisionGroupId changes
  useEffect(() => {
    form.reset(MarginDecisionDefaultValues);
  }, [marginDecisionGroupID, form.reset]);

  // Mutation hooks
  const { submitMarginDecisionMutation, loading: submitLoading } = useSubmitMarginDecisionMutationHook();
  const { commentOnDecisionGroupMutation, loading: commentLoading } = useCommentOnDecisionGroupMutationHook();
  const { useRefetch } = useFetchMarginDecisionsByGroupPolling(marginDecisionGroupID as string, tenderID as string);

  const options = [
    ...(canDecide ? [
      { label: t("common:approve"), value: MarginDecisionEnum.Approve },
      { label: t("common:reject"), value: MarginDecisionEnum.Reject }
    ] : []),
    ...(canDecideAheadOfTurn ? [
      { label: t("chatinputbar.skipapprove", { organizationName: skipOrganizationLabel }), value: SKIP_APPROVE },
      { label: t("chatinputbar.skipreject", { organizationName: skipOrganizationLabel }), value: SKIP_REJECT }
    ] : []),
    ...(canComment ? [
      { label: t("common:comment"), value: MarginDecisionEnum.Comment }
    ] : []),
  ];

  /**
   * Handles the submission of the tender decision form.
   *
   * @param {TMarginDecisionSchema} data - The form data containing the action and comment.
   *
   * @param data.decision - The action selected by the user (approve or reject).
   * @param data.comment - The comment provided by the user.
   */
  const onSubmit: SubmitHandler<TMarginDecisionSchema> = async (data: TMarginDecisionSchema) => {
    const { decision, comment } = data;
    // Process mentions in the comment before submitting
    const decideAheadOfTurn = isSkipOption(decision);
    const mappedDecision = decision === SKIP_APPROVE
      ? MarginDecisionEnum.Approve
      : decision === SKIP_REJECT
        ? MarginDecisionEnum.Reject
        : decision;
    let processedComment = transformMessageForSubmission(comment, tenderUsers);
    if (decideAheadOfTurn) {
      processedComment = prefixSkipComment(processedComment, awaitingOrganizationName, t);
    }

    try {
      if(mappedDecision !== MarginDecisionEnum.Comment) {
        await submitMarginDecisionMutation({
          variables: {
            marginDecisionGroupId: Number(marginDecisionGroupID),
            comment: processedComment, // transformed mention content
            decision: mappedDecision,
            decideAheadOfTurn,
          },
        });
      }
      else {
        await commentOnDecisionGroupMutation({
          variables: {
            groupId: Number(marginDecisionGroupID),
            comment: processedComment,
            type: CommentEnum.MarginDecision
          }
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      // Trigger refetch of data onSubmit
      // To ensure we have the latest permissions data
      useRefetch(tenderState);
      // Close the decision confirmation dialog
      setDecisionConfirmationOpen(false);
      // Reset the form
      form.reset(MarginDecisionDefaultValues);
    }
  };

  return (
    <>
      {userHasPermission ? (
        <Card className={cn("px-2 py-4", hasFinalDecision && "cursor-not-allowed")}>
          <form id="margin-decision-form" className="flex gap-2 items-start flex-1" onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            {/* Decision Dropdown */}
            {<DropdownSelect
              options={options}
              placeholder={t("chatinputbar.selectaction")}
              onChange={(value) => {
                form.setValue("decision", value as any);
                form.trigger(["decision", "comment"]);
              }}
              value={decisionValue}
              error={form.formState.errors.decision?.message}
              disabled={hasFinalDecision}
              currentLanguage={currentLanguage as string}
            />}

            {/* Comment Input with Mentions */}
            <div className="flex-1 relative">
              <Input
                ref={(e) => {
                  commentRef(e);
                  commentInputRef.current = e;
                }}
                value={commentValue}
                onChange={(e) => {
                  commentOnChange(e);
                  handleMentionDetection(e, setMentionQuery);
                }}
                autoComplete="off"
                className={cn(form.formState.errors.comment && "border-red-500 border-2")}
                disabled={hasFinalDecision}
                onKeyDown={decisionValue === MarginDecisionEnum.Comment ? undefined : (e) => handleOnKeyDown(e, form, setDecisionConfirmationOpen)}
                placeholder={t("chatinputbar.comment")}
                {...commentRest}
              />
              {mentionQuery !== null && filteredUsers.length > 0 && (
                <ChatMentionsPopup
                  filteredUsers={filteredUsers}
                  inputRef={commentInputRef as RefObject<HTMLInputElement | HTMLTextAreaElement>}
                  form={form}
                  formField="comment"
                  setMentionQuery={setMentionQuery}
                />
              )}
            </div>
            <div className="flex gap-2">
              <Button
                disabled={hasFinalDecision || !form.formState.isValid || submitLoading || commentLoading}
                type={decisionValue === MarginDecisionEnum.Comment ? "submit" : "button"}
                onClick={decisionValue === MarginDecisionEnum.Comment ? undefined : () => setDecisionConfirmationOpen(true)}
                className="gap-2"
              >
                <CircleLoader visible={submitLoading || commentLoading} height={16} width={16} strokeWidth={2} />
                {
                  submitLoading || commentLoading ?
                    t("common:buttons.submitting") :
                    t("common:buttons.submit")
                }
              </Button>
            </div>
          </form>
        </Card>) : (
        <Card className="p-4 flex flex-row gap-2 items-center">
          <TriangleAlert className="w-4 h-4 text-gray-400" />
          <p className="text-sm text-gray-400">{t("chatinputbar.nopermission")}</p>
        </Card>
      )}
      <DecisionConfirmationAlertDialog
        open={decisionConfirmationOpen}
        onOpenChange={setDecisionConfirmationOpen}
        userDecision={
          decisionValue === SKIP_APPROVE
            ? MarginDecisionEnum.Approve
            : decisionValue === SKIP_REJECT
              ? MarginDecisionEnum.Reject
              : decisionValue as MarginDecisionEnum
        }
        isSkip={isSkipSelected}
        awaitingOrganizationName={skipOrganizationLabel}
        loading={submitLoading || commentLoading}
      />
    </>
  );
};

export default ChatInputBar;
